import {
  Avatar,
  Box,
  Button,
  Chip,
  ChipDelete,
  Divider,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Typography,
} from "@mui/joy";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { NerdPhoneField } from "@nerdjs/nerd-ui";
import { useEffect, useState } from "react";
import { Contact, Contact_Entity } from "../entities/contact";
import { Member_Entity } from "../entities/member";
import Upload from "../hooks/fileUpload/fileUpload";
import { InstrumentsPopover } from "../hooks/instrumentsPopover/instrumentsPopover";
import { useInstruments } from "../redux/instrument/instrumentHooks";
import { useUpdateMemberMutation } from "../redux/member/memberEndpoints";
import { useMyMember } from "../redux/member/memberHooks";
import {
  useCreateMemberInstrumentMutation,
  useDeleteMemberInstrumentMutation,
} from "../redux/memberInstrument/memberInstrumentEndpoints";
import {
  useCreateContactMutation,
  useDeleteContactMutation,
  useUpdateContactMutation,
} from "../redux/contact/contactEndpoints";
import { useDispatch } from "react-redux";
import { local420Api } from "../redux/api/local420Api";
import { useNavigate } from "react-router-dom";
import { RouterConfig } from "../hooks/config/routerConfig";
import {
  FieldLabel,
  GroupCaption,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../hooks/formKit/formKit";
import { CustomFieldsSection } from "../hooks/customFields/customFieldsSection";
import { useUpdateMyCustomFieldMutation } from "../redux/customField/customFieldEndpoints";

/**
 *
 * @returns {ReactElement} Profile page
 */
export function Profile() {
  const { member: _member } = useMyMember();
  const { instrumentsMap } = useInstruments();
  const navigate = useNavigate();
  const [createMusicianInstrument] = useCreateMemberInstrumentMutation();
  const [deleteMusicianInstrument] = useDeleteMemberInstrumentMutation();
  const dispatch = useDispatch();
  const [contactEditIndex, setContactEditIndex] = useState<number>();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updateMember] = useUpdateMemberMutation();
  const [deleteContact] = useDeleteContactMutation();
  const [createContact] = useCreateContactMutation();
  const [updateContact] = useUpdateContactMutation();
  const [member, setMember] = useState<Partial<Member_Entity>>();
  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLAnchorElement) | null
  >(null);
  // Pending "Additional Info" values, keyed "organizationID:noteTypeID" —
  // saved with the rest of the profile on Save.
  const [customFieldEdits, setCustomFieldEdits] = useState<
    Record<string, string>
  >({});
  const [updateCustomField] = useUpdateMyCustomFieldMutation();

  useEffect(() => {
    setMember(_member);
  }, [_member]);

  const save = async () => {
    setLoading(true);
    await updateMember({ id: member?.id ?? 0, body: { ...member } }).unwrap();
    const proms: Promise<any>[] = [];

    // Reconcile the member's instruments to a unique set of instrumentIDs.
    // Treating them as a set (not a list) both prevents duplicates and heals
    // any that already exist: we keep at most one row per instrument, delete
    // deselected rows and any extra duplicate copies, and create what's missing.
    const desiredInstrumentIDs = new Set<number>(
      (member?.memberInstruments ?? []).map((mi) => mi.instrumentID)
    );
    const keptInstrumentIDs = new Set<number>();
    _member?.memberInstruments?.forEach((mi) => {
      const wanted = desiredInstrumentIDs.has(mi.instrumentID);
      const isDuplicate = keptInstrumentIDs.has(mi.instrumentID);
      if (!wanted || isDuplicate) {
        proms.push(deleteMusicianInstrument(mi.id).unwrap());
      } else {
        keptInstrumentIDs.add(mi.instrumentID);
      }
    });
    desiredInstrumentIDs.forEach((instrumentID) => {
      if (!keptInstrumentIDs.has(instrumentID)) {
        proms.push(
          createMusicianInstrument({
            memberID: member.id,
            instrumentID,
          }).unwrap()
        );
      }
    });

    _member?.contacts.forEach((_c) => {
      // Delete old one
      if (!member?.contacts?.find((c) => c.id === _c.id)) {
        proms.push(deleteContact(_c.id).unwrap());
      }
    });

    member?.contacts?.forEach((c) => {
      // Add new
      if (!_member?.contacts.find((_c) => _c.id === c.id)) {
        proms.push(createContact({ ...c, memberID: member.id }).unwrap());
      }
    });

    member?.contacts?.forEach((c) => {
      // Update
      const found = _member?.contacts.find((_c) => _c.id === c.id);
      if (found && c.changed) {
        proms.push(updateContact({ id: found.id, body: { ...c } }).unwrap());
      }
    });

    Object.entries(customFieldEdits).forEach(([key, value]) => {
      const [organizationID, noteTypeID] = key.split(":").map(Number);
      proms.push(
        updateCustomField({ organizationID, noteTypeID, value }).unwrap()
      );
    });

    await Promise.all(proms);
    setCustomFieldEdits({});

    dispatch(local420Api.util.invalidateTags(["members"]));

    setLoading(false);
    setEdit(false);
  };

  if (!member || !instrumentsMap) return <Box />;

  // iOS Settings-style grouped card (white on the gray page).
  const groupCardSx = {
    borderRadius: "14px",
    overflow: "hidden",
    background: "var(--mui-palette-background-surface, #fff)",
    border: "1px solid rgba(120, 120, 128, 0.16)",
  } as const;
  // One entry per instrument (the member can carry duplicate rows).
  const uniqueInstruments = Array.from(
    new Map(
      (member.memberInstruments ?? []).map((mi) => [mi.instrumentID, mi])
    ).values()
  );
  const fullName =
    [member.firstName, member.lastName].filter(Boolean).join(" ") ||
    "Your name";

  return (
    <Box
      sx={{
        padding: 2,
        display: "flex",
        justifyContent: "start",
        gap: 2,
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "end",
          position: "sticky",
          top: 98,
          pt: 1,
          pb: 1,
          zIndex: 999,
          background: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {edit ? (
            <Button
              startDecorator={<i className="fa-solid fa-xmark"></i>}
              variant="plain"
              color="neutral"
              size="sm"
              sx={pillButtonSx}
              onClick={() => {
                setMember(_member);
                setCustomFieldEdits({});
                setEdit(false);
              }}
            >
              Cancel
            </Button>
          ) : (
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
              }}
            >
              Profile
            </Typography>
          )}
          <Button
            startDecorator={
              edit ? (
                <i className="fa-solid fa-check"></i>
              ) : (
                <i className="fa-solid fa-pen"></i>
              )
            }
            loading={loading}
            variant={edit ? "solid" : "soft"}
            color={edit ? "primary" : "neutral"}
            size="sm"
            sx={{ ...pillButtonSx, px: 2.5 }}
            onClick={() => {
              if (edit) {
                save();
              } else {
                setEdit((e) => !e);
              }
            }}
          >
            {edit ? "Save" : "Edit"}
          </Button>
        </Box>
      </Box>
      {/* Avatar hero */}
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          pb: 1,
        }}
      >
        {edit ? (
          <Upload
            cropDimension={{ width: 512, height: 512 }}
            label="Upload Avatar"
            onUpload={(f) => setMember((m) => ({ ...m, avatar: f }))}
          >
            <Box sx={{ position: "relative", cursor: "pointer" }}>
              <Avatar
                src={member?.avatar}
                sx={{
                  height: 116,
                  width: 116,
                  fontSize: 44,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                }}
              >
                <i className="fa-duotone fa-user-music"></i>
              </Avatar>
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: "var(--mui-palette-primary-500, #0A84FF)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "3px solid #F5F5F7",
                  fontSize: 13,
                }}
              >
                <i className="fa-solid fa-camera"></i>
              </Box>
            </Box>
          </Upload>
        ) : (
          <Avatar
            src={member?.avatar}
            sx={{
              height: 116,
              width: 116,
              fontSize: 44,
              boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
            }}
          >
            <i className="fa-duotone fa-user-music"></i>
          </Avatar>
        )}
        <Typography
          sx={{
            mt: 1,
            fontSize: "1.625rem",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            textAlign: "center",
          }}
        >
          {fullName}
        </Typography>
        {member?.companyName || member?.nickName ? (
          <Typography
            level="body2"
            sx={{ color: "var(--mui-palette-text-secondary)" }}
          >
            {member?.companyName || member?.nickName}
          </Typography>
        ) : (
          []
        )}
        {edit && member?.avatar ? (
          <Typography
            level="body3"
            onClick={() => setMember((m) => ({ ...m, avatar: "" }))}
            sx={{
              mt: 0.5,
              color: "#C9251C",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Remove Photo
          </Typography>
        ) : (
          []
        )}
      </Box>

      {/* My QR Code */}
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <GroupCaption>Sharing</GroupCaption>
        <Sheet variant="plain" sx={groupCardSx}>
          <Box
            onClick={() => navigate(RouterConfig.qrCode)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                background: tileColors.blue,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className="fa-solid fa-qrcode"></i>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography>My QR Code</Typography>
              <Typography level="body3">
                Let contractors scan to add you to their address book
              </Typography>
            </Box>
            <i
              className="fa-solid fa-chevron-right"
              style={{ opacity: 0.3 }}
            ></i>
          </Box>
        </Sheet>
      </Box>
      {/* Personal Information */}
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <GroupCaption>Personal Information</GroupCaption>
        <Sheet variant="plain" sx={groupCardSx}>
          <InfoRow
            icon="fa-solid fa-signature"
            color={tileColors.blue}
            label="First Name"
            value={member?.firstName}
            edit={edit}
            onChange={(v) => setMember((m) => ({ ...m, firstName: v }))}
          />
          <Divider />
          <InfoRow
            icon="fa-solid fa-signature"
            color={tileColors.indigo}
            label="Middle Name"
            value={member?.middleName}
            edit={edit}
            onChange={(v) => setMember((m) => ({ ...m, middleName: v }))}
          />
          <Divider />
          <InfoRow
            icon="fa-solid fa-signature"
            color={tileColors.purple}
            label="Last Name"
            value={member?.lastName}
            edit={edit}
            onChange={(v) => setMember((m) => ({ ...m, lastName: v }))}
          />
          <Divider />
          <InfoRow
            icon="fa-solid fa-face-smile"
            color={tileColors.pink}
            label="Nickname"
            value={member?.nickName}
            edit={edit}
            onChange={(v) => setMember((m) => ({ ...m, nickName: v }))}
          />
          <Divider />
          <InfoRow
            icon="fa-solid fa-briefcase"
            color={tileColors.teal}
            label="Company"
            value={member?.companyName}
            edit={edit}
            onChange={(v) => setMember((m) => ({ ...m, companyName: v }))}
          />
        </Sheet>
      </Box>
      {/* Instruments */}
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <GroupCaption>Instruments</GroupCaption>
        <Sheet
          variant="plain"
          sx={{
            ...groupCardSx,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          {uniqueInstruments.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {uniqueInstruments.map((i) => (
                <Chip
                  key={i.instrumentID}
                  variant="soft"
                  color="neutral"
                  size="lg"
                  sx={{ borderRadius: "10px", fontWeight: 600 }}
                  startDecorator={
                    <i
                      className="fa-solid fa-music"
                      style={{ fontSize: 11, opacity: 0.5 }}
                    ></i>
                  }
                  endDecorator={
                    edit ? (
                      <ChipDelete
                        color="danger"
                        variant="plain"
                        onDelete={() => {
                          setMember((m) => ({
                            ...m,
                            memberInstruments:
                              member?.memberInstruments?.filter(
                                (mi) => mi.instrumentID !== i.instrumentID
                              ),
                          }));
                        }}
                      />
                    ) : undefined
                  }
                >
                  {instrumentsMap![i.instrumentID]?.name}
                </Chip>
              ))}
            </Box>
          ) : (
            <Typography
              level="body2"
              sx={{ color: "var(--mui-palette-text-tertiary)", py: 0.25 }}
            >
              No instruments yet
            </Typography>
          )}
          {edit ? (
            <>
              <Button
                onClick={(e) => setAnchorEl(e.currentTarget)}
                color="primary"
                variant="soft"
                size="sm"
                sx={{ ...pillButtonSx, alignSelf: "flex-start", px: 2 }}
                startDecorator={<i className="fa-solid fa-plus"></i>}
              >
                Add Instruments
              </Button>
              <InstrumentsPopover
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                onSelect={(e) => {
                  setMember((m) => ({
                    ...m,
                    memberInstruments: [
                      ...member.memberInstruments!,
                      ...e.reduce<any[]>((a, v) => {
                        if (
                          !member.memberInstruments!.find(
                            (mi) => mi.instrumentID === v
                          )
                        )
                          a.push({
                            id: 0,
                            instrumentID: v,
                            memberID: member.id,
                          });
                        return a;
                      }, []),
                    ],
                  }));
                }}
              />
            </>
          ) : (
            []
          )}
        </Sheet>
      </Box>
      {/* Contacts */}
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <GroupCaption>Contacts</GroupCaption>
        <Sheet variant="plain" sx={groupCardSx}>
          {(member?.contacts ?? []).length === 0 && !edit ? (
            <Box sx={{ px: 1.5, py: 1.5 }}>
              <Typography
                level="body2"
                sx={{ color: "var(--mui-palette-text-tertiary)" }}
              >
                No contacts
              </Typography>
            </Box>
          ) : (
            []
          )}
          {[...(member?.contacts ?? [])].map((c, index) => {
            const contact = new Contact(c);
            return (
              <Box key={c.id}>
                {index > 0 ? <Divider /> : []}
                <Box
                  onClick={edit ? () => setContactEditIndex(index) : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    minHeight: 52,
                    cursor: edit ? "pointer" : "default",
                    transition: "background 0.15s ease",
                    "&:hover": edit
                      ? { background: "rgba(120,120,128,0.08)" }
                      : undefined,
                  }}
                >
                  <FieldLabel
                    icon={contact.categoryIcon()}
                    color={contactColor(contact.category)}
                    label={contact.getLabel()}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, textAlign: "right" }}>
                    <Typography level="body2" noWrap sx={{ fontWeight: 600 }}>
                      {contact.format()}
                    </Typography>
                  </Box>
                  {edit ? (
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setMember((m) => ({
                          ...m,
                          contacts: (member?.contacts ?? []).filter(
                            (mc) => mc.id !== c.id
                          ),
                        }));
                      }}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </IconButton>
                  ) : (
                    []
                  )}
                </Box>
              </Box>
            );
          })}
          {edit ? (
            <Box>
              {(member?.contacts ?? []).length > 0 ? <Divider /> : []}
              <Box
                onClick={() => {
                  setMember((m) => ({
                    ...m,
                    contacts: [...(member.contacts ?? []), Contact.empty()],
                  }));
                  setContactEditIndex((member.contacts ?? []).length);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  minHeight: 52,
                  cursor: "pointer",
                  "&:hover": { background: "rgba(120,120,128,0.08)" },
                }}
              >
                <FieldLabel icon="fa-solid fa-plus" color={tileColors.green} />
                <Typography
                  level="body2"
                  sx={{
                    fontWeight: 600,
                    color: "var(--mui-palette-primary-main)",
                  }}
                >
                  Add Contact
                </Typography>
              </Box>
            </Box>
          ) : (
            []
          )}
        </Sheet>
        {edit &&
        contactEditIndex != null &&
        member.contacts![contactEditIndex] ? (
          <ContactForm
            onChange={(e) => {
              const _contacts = [...member.contacts!];
              _contacts[contactEditIndex] = new Contact({
                ...e,
                changed: true,
              });
              setMember((m) => ({ ...m, contacts: _contacts }));
            }}
            contact={new Contact(member.contacts![contactEditIndex])}
            onClose={() => setContactEditIndex(undefined)}
          />
        ) : (
          []
        )}
      </Box>
      {/* Additional Info — org-scoped custom fields, grouped by organization */}
      <CustomFieldsSection
        edit={edit}
        edits={customFieldEdits}
        onChange={(key, value) =>
          setCustomFieldEdits((edits) => ({ ...edits, [key]: value }))
        }
      />
      {/* <pre>{JSON.stringify(member, null, 2)}</pre> */}
    </Box>
  );
}

/**
 * A single iOS-Settings style row: a colored icon-tile label on the left and a
 * right-aligned value that becomes an inline input while editing.
 *
 * @param root0 props
 * @param root0.icon FontAwesome class
 * @param root0.color icon tile color
 * @param root0.label field label
 * @param root0.value current value
 * @param root0.placeholder shown when empty
 * @param root0.edit whether the field is editable
 * @param root0.onChange value change handler
 * @returns {ReactElement} InfoRow
 */
function InfoRow({
  icon,
  color,
  label,
  value,
  placeholder = "Not set",
  edit,
  onChange,
}: {
  icon: string;
  color: string;
  label: string;
  value?: string;
  placeholder?: string;
  edit: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        minHeight: 48,
      }}
    >
      <FieldLabel icon={icon} color={color} label={label} />
      {edit ? (
        <Input
          variant="plain"
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          sx={{
            flex: 1,
            background: "transparent",
            "--Input-focusedThickness": "0px",
          }}
          slotProps={{ input: { sx: { textAlign: "right", fontWeight: 600 } } }}
        />
      ) : (
        <Typography
          level="body2"
          noWrap
          sx={{
            flex: 1,
            textAlign: "right",
            fontWeight: 600,
            color: value
              ? "var(--mui-palette-text-primary)"
              : "var(--mui-palette-text-tertiary)",
          }}
        >
          {value || placeholder}
        </Typography>
      )}
    </Box>
  );
}

/**
 * iOS palette color for a contact category's icon tile.
 *
 * @param category contact category
 * @returns {string} hex color
 */
function contactColor(category?: string): string {
  switch (category) {
    case "EMAIL":
      return tileColors.blue;
    case "PHONE":
      return tileColors.green;
    case "ADDRESS":
      return tileColors.orange;
    default:
      return tileColors.gray;
  }
}

function ContactForm({
  contact,
  onChange,
  onClose,
}: {
  contact: Contact;
  onChange: (contact: Contact_Entity) => void;
  onClose: () => void;
}) {
  return (
    <Dialog open onClose={onClose} PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle>
        <Box>
          <Typography
            level="h5"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.01em",
              textTransform: "capitalize",
            }}
            startDecorator={<i className={contact.categoryIcon()}></i>}
          >
            {contact.category?.toLowerCase()}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          width: 450,
          maxWidth: "100%",
          gap: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <GroupCaption>Details</GroupCaption>
        <Sheet variant="soft" sx={groupSheetSx}>
          <Select
            variant="plain"
            sx={{ background: "transparent" }}
            slotProps={{
              button: {
                sx: { justifyContent: "end", textTransform: "capitalize" },
              },
              listbox: { sx: { zIndex: 9999 } },
            }}
            onChange={(e, v) => onChange({ ...contact, category: v! })}
            placeholder="Not set"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-shapes"
                color={tileColors.indigo}
                label="Category"
              />
            }
            value={contact?.category ?? ""}
          >
            {["ADDRESS", "EMAIL", "PHONE"].map((o) => (
              <Option key={o} value={o} sx={{ textTransform: "capitalize" }}>
                {o.toLowerCase()}
              </Option>
            ))}
          </Select>
          <Divider />
          <Input
            variant="plain"
            sx={{ background: "transparent" }}
            slotProps={{
              input: {
                sx: { textAlign: "right" },
              },
            }}
            onChange={(e) => onChange({ ...contact, label: e.target.value })}
            placeholder="Not set"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-tag"
                color={tileColors.gray}
                label="Label"
              />
            }
            value={contact?.label ?? ""}
          />
          {contact.category === "EMAIL" ? (
            <>
              <Divider />
              <Input
                variant="plain"
                sx={{ background: "transparent" }}
                slotProps={{
                  input: {
                    sx: { textAlign: "right" },
                  },
                }}
                onChange={(e) =>
                  onChange({ ...contact, email: e.target.value })
                }
                placeholder="Not set"
                startDecorator={
                  <FieldLabel
                    icon="fa-solid fa-envelope"
                    color={tileColors.blue}
                    label="Email"
                  />
                }
                value={contact?.email ?? ""}
              />
            </>
          ) : (
            []
          )}
          {contact.category === "PHONE" ? (
            <>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  pl: 1.5,
                  pr: 1,
                  alignItems: "center",
                  height: 44,
                  justifyContent: "space-between",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <FieldLabel
                  icon="fa-solid fa-phone"
                  color={tileColors.green}
                  label="Phone"
                />
                <Box
                  sx={{
                    width: 200,
                    height: 40,
                    position: "absolute",
                    top: 6,
                    right: 8,
                  }}
                  className="musician"
                >
                  <NerdPhoneField
                    value={contact.phone ?? ""}
                    onAccept={(e) => {
                      onChange({ ...contact, phone: e });
                    }}
                    textFieldProps={{
                      fullWidth: true,
                      variant: "standard",
                      inputProps: {
                        border: "none",
                        disableUnderline: true,
                        style: { textAlign: "right" },
                      },
                      size: "small",
                      sx: { background: "transparent" },
                    }}
                  />
                </Box>
              </Box>
            </>
          ) : (
            []
          )}
          {contact.category === "ADDRESS" ? (
            <>
              <Divider />
              <Input
                variant="plain"
                sx={{ background: "transparent" }}
                slotProps={{
                  input: {
                    sx: { textAlign: "right" },
                  },
                }}
                onChange={(e) =>
                  onChange({ ...contact, street: e.target.value })
                }
                placeholder="Not set"
                startDecorator={
                  <FieldLabel
                    icon="fa-solid fa-location-dot"
                    color={tileColors.orange}
                    label="Street"
                  />
                }
                value={contact?.street ?? ""}
              />
              <Divider />
              <Input
                variant="plain"
                sx={{ background: "transparent" }}
                slotProps={{
                  input: {
                    sx: { textAlign: "right" },
                  },
                }}
                onChange={(e) => onChange({ ...contact, city: e.target.value })}
                placeholder="Not set"
                startDecorator={
                  <FieldLabel
                    icon="fa-solid fa-city"
                    color={tileColors.teal}
                    label="City"
                  />
                }
                value={contact?.city ?? ""}
              />
              <Divider />
              <Input
                variant="plain"
                sx={{ background: "transparent" }}
                slotProps={{
                  input: {
                    sx: { textAlign: "right" },
                  },
                }}
                onChange={(e) =>
                  onChange({ ...contact, state: e.target.value })
                }
                placeholder="Not set"
                startDecorator={
                  <FieldLabel
                    icon="fa-solid fa-map"
                    color={tileColors.purple}
                    label="State"
                  />
                }
                value={contact?.state ?? ""}
              />
              <Divider />
              <Input
                variant="plain"
                sx={{ background: "transparent" }}
                slotProps={{
                  input: {
                    sx: { textAlign: "right" },
                  },
                }}
                onChange={(e) =>
                  onChange({ ...contact, zipcode: e.target.value })
                }
                placeholder="Not set"
                startDecorator={
                  <FieldLabel
                    icon="fa-solid fa-hashtag"
                    color={tileColors.pink}
                    label="Zipcode"
                  />
                }
                value={contact?.zipcode ?? ""}
              />
              <Divider />
              <Input
                variant="plain"
                sx={{ background: "transparent" }}
                slotProps={{
                  input: {
                    sx: { textAlign: "right" },
                  },
                }}
                onChange={(e) =>
                  onChange({ ...contact, country: e.target.value })
                }
                placeholder="Not set"
                startDecorator={
                  <FieldLabel
                    icon="fa-solid fa-globe"
                    color={tileColors.blue}
                    label="Country"
                  />
                }
                value={contact?.country ?? ""}
              />
            </>
          ) : (
            []
          )}
        </Sheet>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ ...pillButtonSx, px: 3 }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
